import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { Link } from 'react-router-dom';
import {
  downloadCsv,
  formatMoney,
  getFinanceExportUrl,
  getFinanceSummary,
  type FinanceSummary,
} from '../services/adminAnalyticsApi';
import { OutlinedBadge } from '@tms/shared-ui';
import { useLanguage } from '../hooks/useLanguage';
import { ContentPageLayout } from '../components/Shared';

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const MetricCard: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%' }}>
    <Typography variant="h5" color="primary" fontWeight="bold">
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Paper>
);

const FinanceDashboardPage: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<FinanceSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getFinanceSummary(days);
      setData(res.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load finance data');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const k = data?.kpis;

  return (
    <ContentPageLayout>
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" color="primary" fontWeight="bold">
            Finance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Revenue, subscriptions, trials, and failed payments.
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            select
            size="small"
            label="Period"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value={7}>7 days</MenuItem>
            <MenuItem value={30}>30 days</MenuItem>
            <MenuItem value={90}>90 days</MenuItem>
            <MenuItem value={365}>365 days</MenuItem>
          </TextField>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadCsv(getFinanceExportUrl(days), `finance-${days}d.csv`)}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : k ? (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="MRR" value={formatMoney(k.mrr, k.mrrCurrency)} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Net revenue" value={formatMoney(k.netRevenueInPeriod, k.currency)} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Paid subscribers" value={k.paidSubscribers} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Trial users" value={k.trialUsers} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Failed payments" value={k.failedPayments} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard label="Churned (period)" value={k.churnedUsers} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Recent transactions
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.recentTransactions || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">
                            No transactions yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.recentTransactions.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell>
                            {new Date(t.transactionDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Link to={`/${currentLanguage}/users/${t.userId}`}>
                              {t.nickname || t.email || t.userId}
                            </Link>
                          </TableCell>
                          <TableCell>{formatMoney(t.amount, t.currency)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Failed payments
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.failedPayments || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="body2" color="text.secondary">
                            No failed payments in period.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.failedPayments.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell>
                            {new Date(t.transactionDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Link to={`/${currentLanguage}/users/${t.userId}`}>
                              {t.nickname || t.email || t.userId}
                            </Link>
                          </TableCell>
                          <TableCell>{formatMoney(t.amount, t.currency)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Plan performance
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Plan</TableCell>
                      <TableCell align="right">Subscribers</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Trial</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.planPerformance || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            No active plans.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.planPerformance?.map((p) => (
                        <TableRow key={p.planId}>
                          <TableCell>{p.planName}</TableCell>
                          <TableCell align="right">{p.subscribers}</TableCell>
                          <TableCell align="right">{p.paid}</TableCell>
                          <TableCell align="right">{p.trials}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap={1} mb={1}>
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                      Active trials
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      All users currently on trial, sorted by expiry date.
                      {(data?.trialsExpiringSoon?.length ?? 0) > 0
                        ? ` ${data?.trialsExpiringSoon.length} expiring within 7 days.`
                        : ''}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {data?.activeTrials?.length ?? 0} on trial
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell>Trial started</TableCell>
                      <TableCell>Trial ends</TableCell>
                      <TableCell align="right">Days left</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.activeTrials || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary">
                            No active trials.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.activeTrials.map((t) => (
                        <TableRow
                          key={t.subscriptionId}
                          sx={t.expiringSoon ? { bgcolor: 'warning.50' } : undefined}
                        >
                          <TableCell>
                            <Link to={`/${currentLanguage}/users/${t.userId}`}>
                              {t.nickname || t.email || t.userId}
                            </Link>
                            {t.role ? (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {t.role}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>{t.email || '—'}</TableCell>
                          <TableCell>
                            {t.planName || '—'}
                            {t.planPrice != null ? (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {formatMoney(t.planPrice, t.currency || 'EUR')}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>{formatDate(t.trialStartedAt)}</TableCell>
                          <TableCell>{formatDate(t.trialEndDate)}</TableCell>
                          <TableCell align="right">
                            {t.daysRemaining != null ? t.daysRemaining : '—'}
                          </TableCell>
                          <TableCell>
                            {t.expiringSoon ? (
                              <OutlinedBadge label="Expiring soon" color="warning" size="small" />
                            ) : (
                              <OutlinedBadge label="Active" color="success" size="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </>
      ) : null}
    </Box>
    </ContentPageLayout>
  );
};

export default FinanceDashboardPage;
